/**
 * @voltxampere/ui — Symbol registry
 *
 * One frozen array carrying every symbol the v0.1 library ships.
 * `findSymbolById` and `findSymbolByShortcut` are O(20) linear
 * lookups today; that is fast enough for palette UI and Sprint 11
 * canvas spawn-on-keypress, and avoids the cost of re-deriving an
 * index when the canvas never re-imports.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { SCHEMA_TO_UI_SYMBOL } from "./aliases.js";
import { acSource } from "./ac-source.js";
import { capacitor } from "./capacitor.js";
import { currentSource } from "./current-source.js";
import { dcSource } from "./dc-source.js";
import { diode } from "./diode.js";
import { ground } from "./ground.js";
import { inductor } from "./inductor.js";
import { jfet } from "./jfet.js";
import { led } from "./led.js";
import { nmos } from "./nmos.js";
import { node } from "./node.js";
import { npn } from "./npn.js";
import { opAmp } from "./op-amp.js";
import { pmos } from "./pmos.js";
import { pnp } from "./pnp.js";
import { potentiometer } from "./potentiometer.js";
import { pulseSource } from "./pulse-source.js";
import { pwlSource } from "./pwl-source.js";
import { resistor } from "./resistor.js";
import { switchSymbol } from "./switch.js";
import { wire } from "./wire.js";
import { zener } from "./zener.js";
// Sprint 38-1 — BJT datasheet variants (NPN + PNP).
import { bjtNpn2n3904 } from "./variants/bjt-npn-2n3904.js";
import { bjtNpnBc547 } from "./variants/bjt-npn-bc547.js";
import { bjtNpn2n2222 } from "./variants/bjt-npn-2n2222.js";
import { bjtPnp2n3906 } from "./variants/bjt-pnp-2n3906.js";
import { bjtPnpBc557 } from "./variants/bjt-pnp-bc557.js";
// Sprint 38-2 — MOSFET datasheet variants (NMOS + PMOS, TO92/TO220/SOT23).
import { mosfetNmos2n7000 } from "./variants/mosfet-nmos-2n7000.js";
import { mosfetNmosIrf540 } from "./variants/mosfet-nmos-irf540.js";
import { mosfetNmosBss138 } from "./variants/mosfet-nmos-bss138.js";
import { mosfetPmosIrf9540 } from "./variants/mosfet-pmos-irf9540.js";
// Sprint 38-3 — JFET + Zener variants.
import { jfetN2n3819 } from "./variants/jfet-n-2n3819.js";
import { jfetNJ201 } from "./variants/jfet-n-j201.js";
import { zener1n4733 } from "./variants/zener-1n4733.js";
import { zener1n4742 } from "./variants/zener-1n4742.js";
import { zener1n4751 } from "./variants/zener-1n4751.js";
// Sprint 38-4 — Schottky diodes + power resistors.
import { schottky1n5817 } from "./variants/schottky-1n5817.js";
import { schottky1n5819 } from "./variants/schottky-1n5819.js";
import { resistor5w } from "./variants/resistor-5w.js";
import { resistor10w } from "./variants/resistor-10w.js";
// Sprint 39-1 — Op-amp datasheet variants (general-purpose).
import { opampLm358 } from "./variants/opamp-lm358.js";
import { opampLm324 } from "./variants/opamp-lm324.js";
// Sprint 39-3 — Op-amp datasheet variants (FET-input + audio-grade).
import { opampTl072 } from "./variants/opamp-tl072.js";
import { opampTl082 } from "./variants/opamp-tl082.js";
import { opampNe5532 } from "./variants/opamp-ne5532.js";
import { opampOpa2134 } from "./variants/opamp-opa2134.js";
// Sprint 39-4 — Op-amp comparator variants (open-collector output).
import { opampLm393 } from "./variants/opamp-lm393.js";
import { opampLm339 } from "./variants/opamp-lm339.js";
// Sprint 39-5 — Op-amp instrumentation amp variant.
import { opampIna126 } from "./variants/opamp-ina126.js";
// Sprint 40-1 — Linear voltage regulator variants (fixed positive +
// fixed negative + LDO). All map to canonical `voltage-source`
// engine type via schemaType — vdc stamp reuse, engine zero-diff.
import { regulator7805 } from "./variants/regulator-7805.js";
import { regulator7812 } from "./variants/regulator-7812.js";
import { regulator7905 } from "./variants/regulator-7905.js";
import { regulatorAms1117_3v3 } from "./variants/regulator-ams1117-3v3.js";
// Sprint 40-2 — Adjustable + second LDO regulators (LM317, LM337,
// AMS1117-5.0). Same canonical engine type.
import { regulatorLm317 } from "./variants/regulator-lm317.js";
import { regulatorLm337 } from "./variants/regulator-lm337.js";
import { regulatorAms1117_5v } from "./variants/regulator-ams1117-5v.js";
// Sprint 41-4 — TTL 7400 series logic IC variants (engine extension
// per ADR-0061). All map to canonical `digital-gate` engine type.
import { logic7400 } from "./variants/logic-7400.js";
import { logic7402 } from "./variants/logic-7402.js";
import { logic7404 } from "./variants/logic-7404.js";
import { logic7408 } from "./variants/logic-7408.js";
import { logic7432 } from "./variants/logic-7432.js";
import { logic7486 } from "./variants/logic-7486.js";
import { logic7474 } from "./variants/logic-7474.js";
// Sprint 41-5 — CMOS 4000 series logic IC variants. Same canonical
// engine type as the TTL family; rail-to-rail 5 V default supply.
// 4017/4051 ship as placeholders (sequential / analog-switching
// behavior arrives Sprint 42+ / 43+ per ADR-0061).
import { logic4001 } from "./variants/logic-4001.js";
import { logic4011 } from "./variants/logic-4011.js";
import { logic4070 } from "./variants/logic-4070.js";
import { logic4017 } from "./variants/logic-4017.js";
import { logic4051 } from "./variants/logic-4051.js";
// Sprint 42-1 — 74HC CMOS logic IC variants. Same canonical
// `digital-gate` engine type as the Sprint 41 TTL/CMOS families.
// 74HC14 (Schmitt) + 74HC74 (D-flop) ship as placeholders pending
// future smooth-threshold + transient-state-machine work.
import { logic74hc00 } from "./variants/logic-74hc00.js";
import { logic74hc04 } from "./variants/logic-74hc04.js";
import { logic74hc08 } from "./variants/logic-74hc08.js";
import { logic74hc14 } from "./variants/logic-74hc14.js";
import { logic74hc74 } from "./variants/logic-74hc74.js";
// Sprint 42-2 — Buck switching regulator variants. vdc stamp reuse
// + idealized V_out; PWM ripple deferred to Sprint 50+ per ADR-0061.
import { regulatorLm2596 } from "./variants/regulator-lm2596.js";
import { regulatorMp1584 } from "./variants/regulator-mp1584.js";
// Sprint 42-3 — Voltage reference variants (LM4040 fixed family +
// TL431 adjustable shunt + ZR285 low-power). Same vdc stamp reuse.
import { referenceLm4040_2v5 } from "./variants/reference-lm4040-2v5.js";
import { referenceLm4040_5v } from "./variants/reference-lm4040-5v.js";
import { referenceLm4040_10v } from "./variants/reference-lm4040-10v.js";
import { referenceTl431 } from "./variants/reference-tl431.js";
import { referenceZr285 } from "./variants/reference-zr285.js";
// Sprint 43-5 — Microcontroller placeholder variants. Each MCU maps
// to canonical `digital-gate` engine type with `gateFunction: "NOT"`
// pending the Sprint 46+ engine extension #4 (full MCU simulation).
import { mcuAtmega328p } from "./variants/mcu-atmega328p.js";
import { mcuAtmega2560 } from "./variants/mcu-atmega2560.js";
import { mcuAttiny85 } from "./variants/mcu-attiny85.js";
import { mcuAttiny84 } from "./variants/mcu-attiny84.js";
import { mcuStm32f103c8 } from "./variants/mcu-stm32f103c8.js";
import { mcuEsp32Wroom32 } from "./variants/mcu-esp32-wroom32.js";
import { mcuRp2040 } from "./variants/mcu-rp2040.js";
import { mcuEsp8266 } from "./variants/mcu-esp8266.js";
// Sprint 43-6 — Analog mux full-simulation variants (4051 update +
// 4052 / 4067 / 74HC4051 new) under the new `analog-mux-8` /
// `analog-mux-16` canonical engine types (ADR-0063).
import { logic4052 } from "./variants/logic-4052.js";
import { logic4067 } from "./variants/logic-4067.js";
import { logic74hc4051 } from "./variants/logic-74hc4051.js";
// Sprint 43-6 — Photo / optoelectronic sensor variants (5).
import { sensorLdr } from "./variants/sensor-ldr.js";
import { sensorBpw34 } from "./variants/sensor-bpw34.js";
import { sensorBc547LedPhoto } from "./variants/sensor-bc547-led-photo.js";
import { sensorTsl2561 } from "./variants/sensor-tsl2561.js";
// Sprint 48-7 — I2C sensor activations (TSL2561 / DS3231 / BMP280).
import { sensorDs3231 } from "./variants/sensor-ds3231.js";
import { sensorBmp280 } from "./variants/sensor-bmp280.js";
// Sprint 48-8 — 5 new sensor variants.
import { sensorMpu6050 } from "./variants/sensor-mpu6050.js";
import { sensorHcSr04 } from "./variants/sensor-hc-sr04.js";
import { sensorMq2 } from "./variants/sensor-mq-2.js";
import { sensorBme680 } from "./variants/sensor-bme680.js";
import { sensorBmp180 } from "./variants/sensor-bmp180.js";
// Sprint 49-6 — 5 USB-serial bridge components.
import { icFt232rl } from "./variants/ic-ft232rl.js";
import { icCp2102 } from "./variants/ic-cp2102.js";
import { icCh340g } from "./variants/ic-ch340g.js";
import { icMax232 } from "./variants/ic-max232.js";
import { circuitDtrRtsReset } from "./variants/circuit-dtr-rts-reset.js";
// Sprint 49-7 — 5 connector + interface components.
import { icPc817Uart } from "./variants/ic-pc817-uart.js";
import { connectorDb9Male } from "./variants/connector-db9-male.js";
import { connectorRs232Cable } from "./variants/connector-rs232-cable.js";
import { connectorJtagHeader } from "./variants/connector-jtag-header.js";
import { connectorIcspHeader } from "./variants/connector-icsp-header.js";
// Sprint 50-8 — 5 preset-required parts (potentiometers + connectors).
import { potTrim10Turn } from "./variants/pot-trim-10-turn.js";
import { potMultiTurn25 } from "./variants/pot-multi-turn-25.js";
import { connectorDin5pin } from "./variants/connector-din-5pin.js";
import { connector9vClip } from "./variants/connector-9v-clip.js";
import { connectorAlligatorClips } from "./variants/connector-alligator-clips.js";
// Sprint 51-1 — Aşama 4 closure 11 polish components (199 → 210).
import { battery9vAlkaline } from "./variants/battery-9v-alkaline.js";
import { sourceSolarPanel } from "./variants/source-solar-panel.js";
import { buzzerActive } from "./variants/buzzer-active.js";
import { buzzerPassive } from "./variants/buzzer-passive.js";
import { ledRed5mm } from "./variants/led-red-5mm.js";
import { ledGreen5mm } from "./variants/led-green-5mm.js";
import { ledBlue5mm } from "./variants/led-blue-5mm.js";
import { ledYellow5mm } from "./variants/led-yellow-5mm.js";
import { ledWhite5mm } from "./variants/led-white-5mm.js";
import { potTrimSingleTurn } from "./variants/pot-trim-single-turn.js";
import { sensorLdr7mm } from "./variants/sensor-ldr-7mm.js";
import { switchReedMagnetic } from "./variants/switch-reed-magnetic.js";
import { sensorTcrt5000 } from "./variants/sensor-tcrt5000.js";
// Sprint 43-6 — Temperature sensor variants (5).
import { sensorNtc10k } from "./variants/sensor-ntc-10k.js";
import { sensorPtc } from "./variants/sensor-ptc.js";
import { sensorLm35 } from "./variants/sensor-lm35.js";
import { sensorDs18b20 } from "./variants/sensor-ds18b20.js";
import { sensorDht11 } from "./variants/sensor-dht11.js";
import { sensorDht22 } from "./variants/sensor-dht22.js";
// Sprint 43-7 — Schmitt trigger variants (74HC14 update + 74HC132 +
// 4584 new). All ride on the digital-gate Schmitt branch (ADR-0064).
import { logic74hc132 } from "./variants/logic-74hc132.js";
import { logic4584 } from "./variants/logic-4584.js";
// Sprint 43-7 — Display placeholder variants (5).
import { display7segCc } from "./variants/display-7seg-cc.js";
import { display7segCa } from "./variants/display-7seg-ca.js";
import { displayLcd16x2 } from "./variants/display-lcd-16x2.js";
import { displayLcd20x4 } from "./variants/display-lcd-20x4.js";
import { displayLedMatrix8x8 } from "./variants/display-led-matrix-8x8.js";
// Sprint 47.5-2 — MCU board + breakout variants (7). All ride on
// canonical mcu-execution engine type (Sprint 46 ADR-0067, Sprint 47
// activation). board-level visual differences only at this stage —
// regulator + crystal + USB sub-circuit decomposition Sprint 51 polish.
import { mcuArduinoUnoR3 } from "./variants/mcu-arduino-uno-r3.js";
import { mcuArduinoNano } from "./variants/mcu-arduino-nano.js";
import { mcuBreakoutDip28 } from "./variants/mcu-breakout-dip28.js";
import { mcuBreakoutDip40 } from "./variants/mcu-breakout-dip40.js";
import { mcuAdafruitFeather } from "./variants/mcu-adafruit-feather.js";
import { mcuSparkfunProMicro } from "./variants/mcu-sparkfun-pro-micro.js";
import { mcuStm32BluePill } from "./variants/mcu-stm32-blue-pill.js";
// Sprint 44-2 — Power device variants (7). TRIAC shipped Sprint 54.2b
// (canonical `triac` engine type with state machine + latch, engine
// ext #5, ADR-0087). SCR + GTO shipped Sprint 54.4 (TRIAC alias via
// `unidirectional` / `gateTurnOff` params, ADR-0089). DIAC remains on
// `diode` placeholder. IGBT + Power MOSFET → `mosfet-nmos`, Power BJT
// TIP120/TIP125 → `bjt-npn` / `bjt-pnp` reuse.
import { powerTriacBta16 } from "./variants/power-triac-bta16.js";
import { powerScrC106 } from "./variants/power-scr-c106.js";
import { powerIgbtIrg4pc50ud } from "./variants/power-igbt-irg4pc50ud.js";
import { powerMosfetIrfp240 } from "./variants/power-mosfet-irfp240.js";
import { powerBjtTip120 } from "./variants/power-bjt-tip120.js";
import { powerBjtTip125 } from "./variants/power-bjt-tip125.js";
import { powerDiacDb3 } from "./variants/power-diac-db3.js";
// Sprint 44-3 — Switching elements (5). Mevcut Sprint 22 B1
// `switch` stamp reuse; SPDT/DPDT/DIP-4 placeholder (multi-pin
// physical layout Sprint 47+ engine extension #4 ile).
import { switchSpdt } from "./variants/switch-spdt.js";
import { switchDpdt } from "./variants/switch-dpdt.js";
import { switchPushButton } from "./variants/switch-push-button.js";
import { switchTactile } from "./variants/switch-tactile.js";
import { switchDip4 } from "./variants/switch-dip-4.js";
// Sprint 44-4 — Optocoupler & isolation (4). Phototransistor opto
// input side `led` schemaType reuse (galvanic isolation + paired
// output transistor stamp Sprint 47+ engine extension #4 ile).
import { opto4n25 } from "./variants/opto-4n25.js";
import { opto6n137 } from "./variants/opto-6n137.js";
import { optoPc817 } from "./variants/opto-pc817.js";
import { optoTlp521 } from "./variants/opto-tlp521.js";
// Sprint 44-5 — Relay (4). Output contacts canonical `switch`
// schemaType reuse; coil current threshold → contact state coupling
// + SSR'in TRIAC + opto kompoziti Sprint 47+ engine extension #4 ile.
import { relayReed } from "./variants/relay-reed.js";
import { relaySpdt5v } from "./variants/relay-spdt-5v.js";
import { relayDpdt12v } from "./variants/relay-dpdt-12v.js";
import { relaySsr } from "./variants/relay-ssr.js";
// Sprint 44-6 — Transformer + magnetic (4). Mevcut `inductor` stamp
// reuse — coupled inductor stamp YOK olduğundan tek primary winding
// placeholder; mutual inductance M coupling Sprint 47+ engine
// extension #4 ile (coupled inductor stamp + multi-secondary).
import { transformerAudio1to1 } from "./variants/transformer-audio-1to1.js";
import { transformerPower220to12 } from "./variants/transformer-power-220-12.js";
import { transformerCenterTapped } from "./variants/transformer-center-tapped.js";
import { magneticCmChoke } from "./variants/magnetic-cm-choke.js";
// Sprint 44-7 — Mechanical motors (4). DC motor + Stepper → `inductor`
// (winding endüktansı placeholder); Servo + BLDC → `voltage-source`
// (PWM/3-phase coil drive placeholder). Gerçek back-EMF + komütasyon
// + reduktör + sequential phase Sprint 47+ engine extension #4 ile.
import { motorDc } from "./variants/motor-dc.js";
import { motorServoSg90 } from "./variants/motor-servo-sg90.js";
import { motorStepper28byj48 } from "./variants/motor-stepper-28byj48.js";
import { motorBldc } from "./variants/motor-bldc.js";
// Sprint 45-2 — Audio amplifier variants (5). Sprint 39 op-amp
// datasheet pattern reuse — canonical `opamp` schemaType. Class-D
// (PAM8403) + stereo (TDA7297) placeholder; gerçek PWM + dual
// channel BTL Sprint 47+/50+ engine extension #4 ile aktif.
import { audioLm386 } from "./variants/audio-lm386.js";
import { audioTda2030 } from "./variants/audio-tda2030.js";
import { audioTda7297 } from "./variants/audio-tda7297.js";
import { audioPam8403 } from "./variants/audio-pam8403.js";
import { audioHeadphoneAmp } from "./variants/audio-headphone-amp.js";
// Sprint 45-3 — RF variants (5). BFG520 + 2N5109 datasheet RF BJT
// (Sprint 38 BJT pattern reuse), mixer + antenna + RF inductor
// generic placeholder. Gerçek RF S-parameters + radiation pattern
// + Gilbert cell mixer Sprint 47+/50+ engine extension #4 ile.
import { rfBfg520 } from "./variants/rf-bfg520.js";
import { rf2n5109 } from "./variants/rf-2n5109.js";
import { rfMixer } from "./variants/rf-mixer.js";
import { rfAntenna } from "./variants/rf-antenna.js";
import { rfInductorHighQ } from "./variants/rf-inductor-high-q.js";
// Sprint 45-4 — Specialty diode variants (5). TVS + Avalanche
// `zener` schemaType + breakdownVoltage; Varactor + Tunnel +
// Photodiode array `diode` placeholder. Gerçek bidirectional
// clamp + voltage-dependent capacitance + NDR + multi-element
// photodiode Sprint 47+/50+ engine extension #4 ile.
import { diodeTvsP6ke6v8 } from "./variants/diode-tvs-p6ke6v8.js";
import { diodeVaractor } from "./variants/diode-varactor.js";
import { diodeTunnel } from "./variants/diode-tunnel.js";
import { diodePhotodiodeArray } from "./variants/diode-photodiode-array.js";
import { diodeAvalanche } from "./variants/diode-avalanche.js";
// Sprint 45-5 — Memory placeholder variants (5). Sprint 43 MCU
// pattern reuse — `digital-gate` schemaType, gateFunction "NOT".
// I2C/SPI/parallel bus simulation Sprint 47+ engine extension #4.
import { memory24lc256 } from "./variants/memory-24lc256.js";
import { memory25lc080 } from "./variants/memory-25lc080.js";
import { memoryW25q128 } from "./variants/memory-w25q128.js";
import { memoryDs1307 } from "./variants/memory-ds1307.js";
import { memoryM48t02 } from "./variants/memory-m48t02.js";
// Sprint 45-6 — Connectors + crystal (8). Connectors = wire-node
// passthrough (engine'de davranis yok); crystal = inductor LC tank
// placeholder (frequency stability + load cap Sprint 47+'da).
import { connectorUsbA } from "./variants/connector-usb-a.js";
import { connectorUsbC } from "./variants/connector-usb-c.js";
import { connectorJstPh } from "./variants/connector-jst-ph.js";
import { connectorPinHeader } from "./variants/connector-pin-header.js";
import { connectorScrewTerminal } from "./variants/connector-screw-terminal.js";
import { crystal16mhz } from "./variants/crystal-16mhz.js";
import { crystal32k768 } from "./variants/crystal-32k768.js";
import { crystalCeramicResonator } from "./variants/crystal-ceramic-resonator.js";
export const ALL_SYMBOLS = Object.freeze([
    // sources
    dcSource,
    acSource,
    pulseSource,
    pwlSource,
    currentSource,
    // passive
    resistor,
    capacitor,
    inductor,
    potentiometer,
    switchSymbol,
    // connectors
    ground,
    wire,
    node,
    // active
    diode,
    led,
    zener,
    npn,
    pnp,
    nmos,
    pmos,
    jfet,
    opAmp,
    // Sprint 38-1 — BJT datasheet variants (5 yeni).
    bjtNpn2n3904,
    bjtNpnBc547,
    bjtNpn2n2222,
    bjtPnp2n3906,
    bjtPnpBc557,
    // Sprint 38-2 — MOSFET datasheet variants (4 yeni).
    mosfetNmos2n7000,
    mosfetNmosIrf540,
    mosfetNmosBss138,
    mosfetPmosIrf9540,
    // Sprint 38-3 — JFET + Zener datasheet variants (5 yeni: 2 JFET + 3 Zener).
    jfetN2n3819,
    jfetNJ201,
    zener1n4733,
    zener1n4742,
    zener1n4751,
    // Sprint 38-4 — Schottky diodes (2) + power resistors (2).
    schottky1n5817,
    schottky1n5819,
    resistor5w,
    resistor10w,
    // Sprint 39-1 — Op-amp general-purpose variants (LM358, LM324).
    opampLm358,
    opampLm324,
    // Sprint 39-3 — Op-amp FET-input + audio-grade variants.
    opampTl072,
    opampTl082,
    opampNe5532,
    opampOpa2134,
    // Sprint 39-4 — Op-amp comparator variants (LM393, LM339).
    opampLm393,
    opampLm339,
    // Sprint 39-5 — Op-amp instrumentation amp variant (INA126).
    opampIna126,
    // Sprint 40-1 — Linear voltage regulator variants.
    regulator7805,
    regulator7812,
    regulator7905,
    regulatorAms1117_3v3,
    // Sprint 40-2 — Adjustable regulators + AMS1117-5.0.
    regulatorLm317,
    regulatorLm337,
    regulatorAms1117_5v,
    // Sprint 41-4 — TTL 7400 series logic IC variants. All share the
    // canonical `digital-gate` engine type per ADR-0061.
    logic7400,
    logic7402,
    logic7404,
    logic7408,
    logic7432,
    logic7486,
    logic7474,
    // Sprint 41-5 — CMOS 4000 series logic IC variants.
    logic4001,
    logic4011,
    logic4070,
    logic4017,
    logic4051,
    // Sprint 42-1 — 74HC CMOS logic IC variants.
    logic74hc00,
    logic74hc04,
    logic74hc08,
    logic74hc14,
    logic74hc74,
    // Sprint 42-2 — Buck switching regulators.
    regulatorLm2596,
    regulatorMp1584,
    // Sprint 42-3 — Voltage references + adjustable shunt regulator.
    referenceLm4040_2v5,
    referenceLm4040_5v,
    referenceLm4040_10v,
    referenceTl431,
    referenceZr285,
    // Sprint 43-5 — Microcontroller placeholder variants (8). Full
    // GPIO/ADC/PWM/I2C/SPI simulation deferred to Sprint 46+ engine
    // extension #4.
    mcuAtmega328p,
    mcuAtmega2560,
    mcuAttiny85,
    mcuAttiny84,
    mcuStm32f103c8,
    mcuEsp32Wroom32,
    mcuRp2040,
    mcuEsp8266,
    // Sprint 43-6 — Analog mux full-simulation variants (3 new +
    // 4051 update). 4051 was Sprint 41-5 placeholder; now points
    // at the dedicated analog-mux-8 canonical type.
    logic4052,
    logic4067,
    logic74hc4051,
    // Sprint 43-6 — Photo / optoelectronic sensors (5).
    sensorLdr,
    sensorBpw34,
    sensorBc547LedPhoto,
    sensorTsl2561,
    // Sprint 48-7 — DS3231 RTC + BMP280 barometric (I2C activations).
    sensorDs3231,
    sensorBmp280,
    // Sprint 48-8 — 5 new sensor variants.
    sensorMpu6050,
    sensorHcSr04,
    sensorMq2,
    sensorBme680,
    sensorBmp180,
    // Sprint 49-6 — 5 USB-serial bridge components.
    icFt232rl,
    icCp2102,
    icCh340g,
    icMax232,
    circuitDtrRtsReset,
    // Sprint 49-7 — 5 connector + interface components.
    icPc817Uart,
    connectorDb9Male,
    connectorRs232Cable,
    connectorJtagHeader,
    connectorIcspHeader,
    // Sprint 50-8 — 5 preset-required parts.
    potTrim10Turn,
    potMultiTurn25,
    connectorDin5pin,
    connector9vClip,
    connectorAlligatorClips,
    // Sprint 51-1 — Aşama 4 closure 12 polish components.
    battery9vAlkaline,
    sourceSolarPanel,
    buzzerActive,
    buzzerPassive,
    ledRed5mm,
    ledGreen5mm,
    ledBlue5mm,
    ledYellow5mm,
    ledWhite5mm,
    potTrimSingleTurn,
    sensorLdr7mm,
    switchReedMagnetic,
    sensorTcrt5000,
    // Sprint 43-6 — Temperature sensors (5 + DHT22).
    sensorNtc10k,
    sensorPtc,
    sensorLm35,
    sensorDs18b20,
    sensorDht11,
    sensorDht22,
    // Sprint 43-7 — Schmitt-trigger variants (2 new; 74HC14 updated
    // in-place to schmitt outputType).
    logic74hc132,
    logic4584,
    // Sprint 43-7 — Display placeholder variants (5).
    display7segCc,
    display7segCa,
    displayLcd16x2,
    displayLcd20x4,
    displayLedMatrix8x8,
    // Sprint 47.5-2 — MCU board + breakout variants (7). Sprint 46
    // ADR-0067 mcu-execution canonical engine type üzerinde Arduino
    // ekosistemi + breakout kart + STM32 polish.
    mcuArduinoUnoR3,
    mcuArduinoNano,
    mcuBreakoutDip28,
    mcuBreakoutDip40,
    mcuAdafruitFeather,
    mcuSparkfunProMicro,
    mcuStm32BluePill,
    // Sprint 44-2 — Power device variants (7). 7 datasheet-named
    // power devices (TRIAC/SCR/IGBT/Power MOSFET/Power BJT NPN+PNP/
    // DIAC). All canonical engine types reused; gerçek thyristor +
    // bidirectional breakdown + IGBT/Darlington internal structure
    // stamps Sprint 47+ engine extension #4'e ertelendi.
    powerTriacBta16,
    powerScrC106,
    powerIgbtIrg4pc50ud,
    powerMosfetIrfp240,
    powerBjtTip120,
    powerBjtTip125,
    powerDiacDb3,
    // Sprint 44-3 — Switching elements (5). SPDT/DPDT/DIP-4 placeholder
    // single-channel; push button + tactile NO momentary doğal
    // davranış via canonical `switch` stamp.
    switchSpdt,
    switchDpdt,
    switchPushButton,
    switchTactile,
    switchDip4,
    // Sprint 44-4 — Optocoupler & isolation (4). Input LED tarafı
    // visible; output transistor + galvanic isolation Sprint 47+'a.
    opto4n25,
    opto6n137,
    optoPc817,
    optoTlp521,
    // Sprint 44-5 — Relay (4). Output contacts placeholder; coil
    // simulation + SSR iç yapısı Sprint 47+'a.
    relayReed,
    relaySpdt5v,
    relayDpdt12v,
    relaySsr,
    // Sprint 44-6 — Transformer + magnetic (4). Tek primary winding
    // placeholder; coupled inductor stamp + secondary winding +
    // mutual inductance M Sprint 47+ engine extension #4 ile.
    transformerAudio1to1,
    transformerPower220to12,
    transformerCenterTapped,
    magneticCmChoke,
    // Sprint 44-7 — Mechanical motors (4). DC + Stepper winding
    // placeholder; Servo + BLDC PWM/3-phase coil drive placeholder.
    // Back-EMF + komütasyon + sequential phase Sprint 47+'a.
    motorDc,
    motorServoSg90,
    motorStepper28byj48,
    motorBldc,
    // Sprint 45-2 — Audio amplifier variants (5). Sprint 39 op-amp
    // datasheet pattern reuse. PAM8403 (Class-D) + TDA7297 (stereo)
    // placeholder.
    audioLm386,
    audioTda2030,
    audioTda7297,
    audioPam8403,
    audioHeadphoneAmp,
    // Sprint 45-3 — RF variants (5). BFG520 + 2N5109 datasheet BJT;
    // mixer + antenna + RF inductor generic placeholder.
    rfBfg520,
    rf2n5109,
    rfMixer,
    rfAntenna,
    rfInductorHighQ,
    // Sprint 45-4 — Specialty diode variants (5). TVS + Avalanche
    // zener; Varactor + Tunnel + Photodiode array diode placeholder.
    diodeTvsP6ke6v8,
    diodeVaractor,
    diodeTunnel,
    diodePhotodiodeArray,
    diodeAvalanche,
    // Sprint 45-5 — Memory placeholder variants (5). digital-gate
    // canonical placeholder; I2C/SPI/parallel bus simulation Sprint 47+.
    memory24lc256,
    memory25lc080,
    memoryW25q128,
    memoryDs1307,
    memoryM48t02,
    // Sprint 45-6 — Connectors + crystal (8). Connectors wire-node
    // passthrough; crystal inductor LC tank placeholder.
    connectorUsbA,
    connectorUsbC,
    connectorJstPh,
    connectorPinHeader,
    connectorScrewTerminal,
    crystal16mhz,
    crystal32k768,
    crystalCeramicResonator,
]);
/**
 * Lookup a symbol by its identifier.
 *
 * Direct UI-id match wins; if that fails, the schema-canonical
 * alias map (Sprint 25 R2 lane 4a — TS-001 fix) is consulted so
 * circuits authored against the schema canonical types
 * (`voltage-source`, `bjt-npn`, `mosfet-nmos`, `opamp`,
 * `wire-node`, `jfet-n`, `jfet-p`) resolve to their UI symbols
 * without the renderer emitting "No symbol registered" warnings.
 *
 * Engine alias direction (UI → schema canonical) lives in
 * `packages/engine/src/stamps/register.ts` `SCHEMA_SYMBOL_ALIASES`.
 */
export function findSymbolById(id) {
    // Fast path — direct UI id (boot fixture, place commands).
    const direct = ALL_SYMBOLS.find((s) => s.id === id);
    if (direct !== undefined)
        return direct;
    // Slow path — schema canonical fallback (preset library,
    // schema-faithful fixtures).
    const aliased = SCHEMA_TO_UI_SYMBOL[id];
    if (aliased === undefined)
        return undefined;
    return ALL_SYMBOLS.find((s) => s.id === aliased);
}
export function findSymbolByShortcut(shortcut) {
    return ALL_SYMBOLS.find((s) => s.shortcut === shortcut);
}
export function symbolsByCategory(category) {
    return ALL_SYMBOLS.filter((s) => s.category === category);
}
//# sourceMappingURL=registry.js.map